import { Link } from 'gatsby'
import { useMobile } from 'src/hooks/useMobile'
import { InView } from 'react-intersection-observer'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { CallToAction } from '../common/CallToAction'
import { getImageWithCertainResolution } from './helpers'
import type { BannerHighlightItemProps } from './types'

type BannerHighlightItemComponentProps = {
  banner: BannerHighlightItemProps
  index: number
  section?: string
  locationOnPage?: string
  title: string
}

export function BannerHighlightItem({
  banner,
  index,
  locationOnPage,
  section,
  title,
}: BannerHighlightItemComponentProps) {
  const { screenWidth } = useMobile()
  const { pageData } = useGlobalContext()

  const standardObject = getStandardObject(pageData)

  const sendAmplitudeClickEvent = () => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: title ?? banner?.title?.text ?? banner?.text?.body ?? '',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })
  }

  function fontSizeDinamica() {
    const quantText = banner?.title?.text?.length
    const calcFont = (quantText - 8) * 2

    if (screenWidth > 1120) {
      return 45 - calcFont
    }

    if (screenWidth > 1120 && screenWidth < 1300) {
      return 40 - calcFont
    }

    if (screenWidth < 1120) {
      return 22 - calcFont
    }

    return 45
  }

  const sendPromoFeatureViewedEvent = () => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: title ?? banner?.title?.text ?? 'N/A',
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  return (
    <InView
      threshold={0.7}
      onChange={(inView) => {
        if (inView) {
          sendPromoFeatureViewedEvent()
        }
      }}
      triggerOnce
      as="div"
      key={index}
      style={{ backgroundColor: banner?.backgroundColor }}
      className="banner_container w-full relative flex justify-center mt-4 mb-8"
      onClick={() => sendAmplitudeClickEvent()}
      onKeyDown={() => sendAmplitudeClickEvent()}
    >
      {banner?.cta?.enable ? (
        <Link
          to={banner?.linkURL ? banner.linkURL : '#'}
          className="banner_img-container"
        >
          {getImageWithCertainResolution({
            screenWidth,
            banner,
          })}
        </Link>
      ) : (
        <section className="banner_img-container">
          {getImageWithCertainResolution({
            screenWidth,
            banner,
          })}
        </section>
      )}

      <div
        style={{ color: banner?.text?.fontColor }}
        className="banner-info absolute w-full max-w-[16rem] font-normal text-lg mb-4"
      >
        {banner?.title?.enable && banner?.title?.text && (
          <h2
            style={{
              color: banner?.title?.fontColor,
              fontSize: `${fontSizeDinamica()}px`,
            }}
            className="banner-title"
          >
            {banner?.title?.text}
          </h2>
        )}

        {banner?.text?.enable && banner?.text?.body && (
          <p
            style={{ color: banner?.text?.fontColor }}
            className="banner-subtitle"
          >
            {banner?.text?.body}
          </p>
        )}

        {banner?.cta?.enable && (
          <CallToAction
            link={banner?.linkURL}
            text={banner?.cta?.linkText}
            fontColor={banner?.cta?.fontColor}
            backgroundColor={banner?.cta?.backgroundColor}
            hover="#F7F8F9"
          />
        )}
      </div>
    </InView>
  )
}
